import { OrderManagementPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { folderTypes } from "@redotech/redo-views-common/folders";
import { FulfillmentGroupView } from "@redotech/redo-views-common/views";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { sleep } from "@redotech/util/schedule";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { CreateOmsViewFolderModal } from "./common/create-oms-view-folder-modal";
import { EditOmsViewFolderModal } from "./common/edit-oms-view-folder-modal";
import { RedoOutboundLabelsRpcClientContext } from "./outbound-labels-rpc-client";

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { findDefaultOutboundView } from "./outbound-default-view";

export interface OutboundViewsData {
  openCreateFolderModal: () => void;
  openEditFolderModal: (folderId: string) => void;
  handleDeleteFolder: (folderId: string) => void;
  updateOrdering: (
    updatedViews: FulfillmentGroupView[],
    updatedFolders: folderTypes["fulfillment-groups"][],
  ) => void;
  hasInitallyLoaded: boolean;
  views: FulfillmentGroupView[];
  folders: folderTypes["fulfillment-groups"][];
}

export const OutboundViewsContext = createContext<
  OutboundViewsData | undefined
>(undefined);

export type ViewCounts = Record<string, number>;
export type FolderCounts = Record<string, number>;

export const OutboundViewCountsContext = createContext<
  { viewCounts: ViewCounts; folderCounts: FolderCounts } | undefined
>(undefined);

export interface ReloadOutboundViewsOptions {
  onlyReloadIfChanged?: boolean;
}

export const ReloadOutboundViewsContext = createContext<
  ((options?: ReloadOutboundViewsOptions) => Promise<void>) | undefined
>(undefined);

export const OutboundViewsProvider = memo(function OutboundViewsProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const team = useRequiredContext(TeamContext);
  const client = useContext(RedoOutboundLabelsRpcClientContext);

  const [views, setViews] = useState<FulfillmentGroupView[]>([]);
  const [folders, setFolders] = useState<folderTypes["fulfillment-groups"][]>(
    [],
  );
  const [viewCounts, setViewCounts] = useState<Record<string, number>>({});
  const [folderCounts, setFolderCounts] = useState<Record<string, number>>({});
  const [hasInitallyLoaded, setHasInitallyLoaded] = useState(false);

  const hasPermissionToSeeOutboundViews = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  const [_, loadViews] = useTriggerLoad(async (signal) => {
    if (!client || !hasPermissionToSeeOutboundViews) {
      return;
    }

    const { views, folders } = await client.getViewsAndFolders(null, {
      signal,
    });
    setHasInitallyLoaded(true);
    setViews(views);
    setFolders(folders);
  });

  const reloadViews = useHandler(
    async (options?: ReloadOutboundViewsOptions) => {
      if (options?.onlyReloadIfChanged) {
        if (!client || !hasPermissionToSeeOutboundViews) {
          return;
        }
        const result = await client.getViewsAndFolders(null, {});
        setViews((prev) => {
          if (viewsMatch(prev, result.views)) {
            return prev;
          }
          return result.views;
        });
        setFolders((prev) => {
          if (foldersMatch(prev, result.folders)) {
            return prev;
          }
          return result.folders;
        });
      } else {
        loadViews();
      }
    },
  );

  useEffect(() => {
    void reloadViews();
  }, [client, reloadViews, team, hasPermissionToSeeOutboundViews]);

  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState<
    folderTypes["fulfillment-groups"] | null
  >(null);

  const handleCreateFolder = async (name: string) => {
    if (!client) {
      return;
    }
    await client.createViewFolder({ name });
    void reloadViews();
  };

  const handleEditFolder = async (folderId: string, folderName: string) => {
    if (!client) {
      return;
    }
    await client.updateViewFolder({ folderId, name: folderName });
    void reloadViews();
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!client) {
      return;
    }
    await client.deleteViewFolder({ folderId });
    void reloadViews();
  };

  const updateOrdering = async (
    updatedViews: FulfillmentGroupView[],
    updatedFolders: folderTypes["fulfillment-groups"][],
  ) => {
    if (!client) {
      return;
    }
    // Optimistic update
    setViews(updatedViews);
    setFolders(updatedFolders);

    try {
      await client.saveViewsAndFolders({
        views: updatedViews,
        folders: updatedFolders,
      });
    } catch (error) {
      console.error("Error saving view and folder ordering");
      if (error instanceof Error) {
        console.error(error.message);
      } else {
        console.error(error);
      }
    }
  };

  const openCreateFolderModal = useHandler(() => {
    setShowCreateFolderModal(true);
  });

  const openEditFolderModal = useHandler((folderId: string) => {
    const folder = folders?.find((folder) => folder._id === folderId);
    if (folder) {
      setShowEditFolderModal(true);
      setSelectedFolder(folder);
    }
  });

  const reservedFolderNames = useMemo(() => {
    return new Set(folders.map((folder) => folder.name));
  }, [folders]);

  useLoad(
    async (signal) => {
      if (
        !client ||
        !team ||
        !team?.settings?.outboundLabels?.liveViewCountsEnabled ||
        !team?.settings?.outboundLabels?.newOmsEnabled ||
        !hasPermissionToSeeOutboundViews
      ) {
        return;
      }

      let errorCount = 0;
      while (true) {
        try {
          const backoffSeconds =
            errorCount === 0 ? 0 : Math.min(2 ** (errorCount - 1), 15);
          await sleep(
            Temporal.Duration.from({ seconds: backoffSeconds }),
            signal,
          );

          const iterator = client.watchTeamViewCounts(
            { merchantTimezone: team?.ianaTimezone },
            { signal },
          );

          for await (const {
            viewCounts: updatedViewCounts,
            folderCounts: updatedFolderCounts,
          } of iterator) {
            if (updatedViewCounts) {
              setViewCounts(updatedViewCounts);
            }

            if (updatedFolderCounts) {
              setFolderCounts(updatedFolderCounts);
            }

            errorCount = 0;
          }
        } catch (e) {
          if (signal.aborted) {
            break;
          }
          errorCount++;
          console.error("Error while watching for view count updates", e);
          const errMsg = e instanceof Error ? e.message : String(e);
          if (
            errMsg &&
            errMsg.toLowerCase().includes("lacking required permissions")
          ) {
            console.error("Lacking required permissions, stopping watch");
            break;
          }
        }
      }
    },
    [client, team?.ianaTimezone, hasPermissionToSeeOutboundViews],
  );

  return (
    <OutboundViewsContext.Provider
      value={{
        openCreateFolderModal,
        openEditFolderModal,
        handleDeleteFolder,
        updateOrdering,
        views,
        folders,
        hasInitallyLoaded,
      }}
    >
      <ReloadOutboundViewsContext.Provider value={reloadViews}>
        <OutboundViewCountsContext.Provider
          value={{ viewCounts, folderCounts }}
        >
          {children}
          <CreateOmsViewFolderModal
            isOpen={showCreateFolderModal}
            onClose={() => setShowCreateFolderModal(false)}
            onSubmit={handleCreateFolder}
            reservedFolderNames={reservedFolderNames}
          />
          {selectedFolder && (
            <EditOmsViewFolderModal
              deleteFolder={handleDeleteFolder}
              isOpen={showEditFolderModal}
              onClose={() => setShowEditFolderModal(false)}
              onSubmit={handleEditFolder}
              reservedFolderNames={reservedFolderNames}
              selectedFolder={selectedFolder}
            />
          )}
        </OutboundViewCountsContext.Provider>
      </ReloadOutboundViewsContext.Provider>
    </OutboundViewsContext.Provider>
  );
});

function viewsMatch(
  prev: FulfillmentGroupView[],
  next: FulfillmentGroupView[],
): boolean {
  if (prev.length !== next.length) {
    return false;
  }
  for (const [i, element] of prev.entries()) {
    const nextElement = next[i];
    if (
      element._id !== nextElement?._id ||
      String(element.updatedAt) !== String(nextElement?.updatedAt)
    ) {
      return false;
    }
  }
  return true;
}

function foldersMatch(
  prev: folderTypes["fulfillment-groups"][],
  next: folderTypes["fulfillment-groups"][],
): boolean {
  if (prev.length !== next.length) {
    return false;
  }
  for (const [i, element] of prev.entries()) {
    const nextElement = next[i];
    if (
      element._id !== nextElement?._id ||
      String(element.updatedAt) !== String(nextElement?.updatedAt)
    ) {
      return false;
    }
  }
  return true;
}
